import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Share2, Receipt, Trash2, Check, ChevronLeft, Users, X, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApp } from "@/contexts/AppContext";
import Header from "@/components/Header";
import AdBanner from "@/components/AdBanner";
import PageAdLayout from "@/components/PageAdLayout";
import AddExpenseModal from "@/components/AddExpenseModal";
import BillScannerModal from "@/components/BillScannerModal";
import SettleUpModal from "@/components/SettleUpModal";
import ShareModal from "@/components/ShareModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700',
  'bg-purple-100 text-purple-700', 'bg-cyan-100 text-cyan-700',
];

export const MemberBadge = ({ member, index }) => {
  const initials = member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div
      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${COLORS[index % COLORS.length]}`}
      title={member.name}
    >
      {initials}
    </div>
  );
};

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.2 } } };

const GroupPage = () => {
  const { id } = useParams();
  const { t, formatAmount } = useApp();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showSettleUp, setShowSettleUp] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedBill, setScannedBill] = useState(null);

  const [newMember, setNewMember] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [showMemberInput, setShowMemberInput] = useState(false);

  const fetchGroup = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/${id}`);
      setGroup(res.data);
      setNotFound(false);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchGroup(); }, [fetchGroup]);

  const handleAddMember = async () => {
    if (!newMember.trim()) return;
    setAddingMember(true);
    try {
      await axios.post(`${API}/groups/${id}/members`, { name: newMember.trim() });
      setNewMember('');
      setShowMemberInput(false);
      fetchGroup();
    } catch { toast.error(t('error')); }
    finally { setAddingMember(false); }
  };

  const handleDeleteExpense = async (expId) => {
    try {
      await axios.delete(`${API}/groups/${id}/expenses/${expId}`);
      fetchGroup();
      toast.success(t('expense_deleted'));
    } catch { toast.error(t('error')); }
  };

  const handleDeleteSettlement = async (sId) => {
    try {
      await axios.delete(`${API}/groups/${id}/settlements/${sId}`);
      fetchGroup();
    } catch { toast.error(t('error')); }
  };

  const handleBillScanned = (billData) => {
    setScannedBill(billData);
    setShowScanner(false);
    setShowAddExpense(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Receipt size={48} className="mx-auto text-zinc-200 mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-zinc-900 font-outfit">{t('group_not_found')}</h2>
          <p className="text-zinc-500 mt-2 mb-6 text-base">{t('group_not_found_desc')}</p>
          <Link to="/"><Button className="bg-zinc-900 hover:bg-zinc-800 text-white"><ArrowLeft size={14} className="mr-2" />{t('back_to_home')}</Button></Link>
        </div>
      </div>
    );
  }

  const memberMap = Object.fromEntries(group.members.map(m => [m.id, m.name]));

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <Header />

      <PageAdLayout topSection={
        /* Group Banner — full width */
        <div className="bg-white border-b border-zinc-200">
          <div className="max-w-4xl mx-auto px-4 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-1 text-zinc-400 text-xs mb-1">
                <Link to="/" className="hover:text-zinc-700 transition-colors flex items-center gap-1">
                  <ChevronLeft size={11} /> {t('app_name')}
                </Link>
              </div>
              <h1 data-testid="group-name" className="text-2xl sm:text-3xl font-bold text-zinc-900 font-outfit tracking-tight">{group.name}</h1>
              <p className="text-zinc-400 text-sm mt-0.5">
                {group.members.length} {t('members')} · {formatAmount(group.total_expenses)} {t('total_expenses_lower')}
              </p>
              <p className="text-[11px] text-zinc-300 mt-1">{t('data_ttl_note')}</p>
            </div>
            <Button data-testid="share-group-btn" onClick={() => setShowShare(true)} variant="outline" size="sm" className="shrink-0 h-9 border-zinc-200">
              <Share2 size={13} strokeWidth={1.5} className="mr-1.5" /> {t('share_group')}
            </Button>
          </div>

          {/* Members row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {group.members.map((m, i) => (
              <div key={m.id} className="flex items-center gap-1.5 bg-zinc-50 border border-zinc-200 rounded-full pl-1 pr-3 py-0.5">
                <MemberBadge member={m} index={i} />
                <span className="text-sm text-zinc-700">{m.name}</span>
              </div>
            ))}
            {!showMemberInput && (
              <button
                data-testid="add-member-btn"
                onClick={() => setShowMemberInput(true)}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-900 border border-dashed border-zinc-300 rounded-full px-3 py-1.5 transition-all hover:border-zinc-400"
              >
                <Plus size={11} /> {t('add_member')}
              </button>
            )}
          </div>

          {/* Add Member Input */}
          {showMemberInput && (
            <div className="flex items-center gap-2 mb-2">
              <Input
                data-testid="add-member-input"
                placeholder={t('member_name')}
                value={newMember}
                onChange={e => setNewMember(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddMember(); if (e.key === 'Escape') setShowMemberInput(false); }}
                className="h-9 max-w-[200px]"
                autoFocus
              />
              <Button size="sm" onClick={handleAddMember} disabled={addingMember} className="h-9 bg-zinc-900 text-white">
                {addingMember ? '...' : t('add')}
              </Button>
              <button onClick={() => { setShowMemberInput(false); setNewMember(''); }} className="text-zinc-400 hover:text-zinc-700 transition-colors">
                <X size={15} />
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="add-expense-btn" onClick={() => setShowAddExpense(true)} className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white text-sm" disabled={group.members.length === 0}>
              <Plus size={13} strokeWidth={2} className="mr-1.5" /> {t('add_expense')}
            </Button>
            <Button data-testid="scan-bill-btn" onClick={() => setShowScanner(true)} variant="outline" className="h-9 border-zinc-200 text-sm" disabled={group.members.length === 0}>
              <Receipt size={13} strokeWidth={1.5} className="mr-1.5" /> {t('scan_bill')}
            </Button>
            <Button data-testid="settle-up-btn" onClick={() => setShowSettleUp(true)} variant="outline" className="h-9 border-zinc-200 text-sm" disabled={group.members.length < 2}>
              <Check size={13} strokeWidth={2} className="mr-1.5" /> {t('settle_up')}
            </Button>
          </div>
        </div>
        </div>
      }>
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-4">
        <Tabs defaultValue="balances" data-testid="group-tabs">
          <TabsList className="bg-zinc-100 mb-6 h-9">
            <TabsTrigger value="balances" data-testid="tab-balances" className="text-sm">{t('balances')}</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses" className="text-sm">{t('expenses')}</TabsTrigger>
            <TabsTrigger value="bills" data-testid="tab-bills" className="text-sm">{t('bills')}</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="text-sm">{t('history')}</TabsTrigger>
          </TabsList>

          {/* BALANCES TAB */}
          <TabsContent value="balances">
            {group.members.length === 0 ? (
              <div className="text-center py-16 text-zinc-400">
                <Users size={40} className="mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                <p className="text-base">{t('add_members_first')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <motion.div variants={container} initial="hidden" animate="show"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.member_balances.map((mb, i) => (
                    <motion.div key={mb.member_id} variants={item}
                      className={`border rounded-xl p-4 ${mb.balance > 0.01 ? 'border-emerald-200 bg-emerald-50' : mb.balance < -0.01 ? 'border-rose-200 bg-rose-50' : 'border-zinc-200 bg-white'}`}
                      data-testid={`balance-card-${mb.member_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MemberBadge member={{ name: mb.name, id: mb.member_id }} index={i} />
                        <span className="font-medium text-zinc-900 text-sm">{mb.name}</span>
                      </div>
                      <div className={`text-xl font-bold ${mb.balance > 0.01 ? 'text-emerald-600' : mb.balance < -0.01 ? 'text-rose-600' : 'text-zinc-300'}`}>
                        {mb.balance > 0.01 ? '+' : ''}{formatAmount(Math.abs(mb.balance))}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {mb.balance > 0.01 ? t('owed') : mb.balance < -0.01 ? t('owes') : t('settled')}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {group.suggested_settlements.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-3">{t('suggested_settlements')}</p>
                    <motion.div variants={container} initial="hidden" animate="show" className="space-y-2">
                      {group.suggested_settlements.map((s, i) => (
                        <motion.div key={i} variants={item}
                          className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-xl hover:border-zinc-300 transition-all">
                          <div>
                            <div className="text-sm">
                              <span className="font-semibold text-zinc-900">{s.from_name}</span>
                              <span className="text-zinc-400 mx-2">{t('pays')}</span>
                              <span className="font-semibold text-zinc-900">{s.to_name}</span>
                            </div>
                            <div className="text-lg font-bold text-zinc-900 mt-0.5">{formatAmount(s.amount)}</div>
                          </div>
                          <Button size="sm" onClick={() => setShowSettleUp(true)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8">
                            {t('settle_up')}
                          </Button>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                )}

                {group.suggested_settlements.length === 0 && group.expenses.length > 0 && (
                  <div className="text-center py-10 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <Check size={32} className="mx-auto text-emerald-500 mb-2" strokeWidth={2} />
                    <p className="text-emerald-700 font-medium">{t('all_settled')}</p>
                  </div>
                )}

                {/* Ad banner - handled by PageAdLayout */}
              </div>
            )}
          </TabsContent>

          {/* EXPENSES TAB */}
          <TabsContent value="expenses">
            {group.expenses.length === 0 ? (
              <div className="text-center py-16 text-zinc-400">
                <Receipt size={40} className="mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                <p className="text-base">{t('no_expenses')}</p>
                <Button onClick={() => setShowAddExpense(true)} className="mt-5 bg-zinc-900 text-white h-10" disabled={group.members.length === 0}>
                  <Plus size={14} className="mr-1.5" /> {t('add_expense')}
                </Button>
              </div>
            ) : (
              <motion.div variants={container} initial="hidden" animate="show" className="space-y-2">
                {group.expenses.slice().reverse().map(exp => (
                  <motion.div key={exp.id} variants={item}
                    className="flex items-start gap-3 p-4 bg-white border border-zinc-200 rounded-xl hover:border-zinc-300 transition-all group/exp"
                    data-testid={`expense-item-${exp.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-zinc-900">{exp.description}</div>
                        <div className="text-right shrink-0">
                          <div className="font-bold text-zinc-900">{formatAmount(exp.amount)}</div>
                          {exp.date && <div className="text-xs text-zinc-400">{exp.date}</div>}
                        </div>
                      </div>
                      <div className="text-sm text-zinc-400 mt-1">
                        {t('paid_by')} <span className="text-zinc-600 font-medium">{memberMap[exp.paid_by] || '?'}</span>
                        {' · '}{exp.participants.length} {t('people')}
                        {exp.participants.length > 0 && (
                          <span> ({formatAmount(exp.amount / exp.participants.length)} {t('each')})</span>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-expense-${exp.id}`}
                      onClick={() => handleDeleteExpense(exp.id)}
                      className="opacity-30 hover:opacity-100 transition-opacity text-zinc-400 hover:text-rose-500 p-1 mt-0.5 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>

          {/* BILLS TAB */}
          <TabsContent value="bills">
            {(!group.bills || group.bills.length === 0) ? (
              <div className="text-center py-16 text-zinc-400">
                <Receipt size={40} className="mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                <p className="text-base">{t('no_bills')}</p>
              </div>
            ) : (
              <motion.div variants={container} initial="hidden" animate="show" className="space-y-3">
                {group.bills.slice().reverse().map(bill => (
                  <motion.div key={bill.id} variants={item}
                    className="p-4 bg-white border border-zinc-200 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-semibold text-zinc-900">{bill.title || t('scan_bill')}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {bill.created_at && bill.created_at.split('T')[0]}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-zinc-900">{formatAmount(bill.total)}</div>
                        <div className="text-xs text-zinc-400">{bill.items?.length || 0} {t('items_count')}</div>
                      </div>
                    </div>
                    <div className="space-y-1.5 pt-3 border-t border-zinc-100">
                      {(bill.items || []).map((it, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-zinc-600 flex items-center gap-1.5">
                            {it.is_tax && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{t('tax')}</span>
                            )}
                            {it.name}
                          </span>
                          <span className="text-zinc-900 font-medium">{Number(it.amount).toLocaleString('id-ID')}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history">
            {group.settlements.length === 0 ? (
              <div className="text-center py-16 text-zinc-400">
                <DollarSign size={40} className="mx-auto mb-3 opacity-20" strokeWidth={1.5} />
                <p className="text-base">{t('no_settlements')}</p>
              </div>
            ) : (
              <motion.div variants={container} initial="hidden" animate="show" className="space-y-2">
                {group.settlements.slice().reverse().map(s => (
                  <motion.div key={s.id} variants={item}
                    className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-xl group/stl"
                    data-testid={`settlement-item-${s.id}`}
                  >
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-zinc-900">{memberMap[s.from_member] || '?'}</span>
                        <span className="text-zinc-400 mx-2">{t('paid')}</span>
                        <span className="font-medium text-zinc-900">{memberMap[s.to_member] || '?'}</span>
                      </div>
                      <div className="font-bold text-emerald-600 mt-0.5">{formatAmount(s.amount)}</div>
                      {s.created_at && <div className="text-xs text-zinc-400 mt-0.5">{s.created_at.split('T')[0]}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-600 font-semibold px-2.5 py-1 rounded-full border border-emerald-200">{t('settled')}</span>
                      <button
                        onClick={() => handleDeleteSettlement(s.id)}
                        className="opacity-30 hover:opacity-100 transition-opacity text-zinc-400 hover:text-rose-500 p-1"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      </PageAdLayout>

      {/* Modals */}
      {showAddExpense && (
        <AddExpenseModal
          group={group}
          scannedBill={scannedBill}
          onSuccess={() => { setScannedBill(null); fetchGroup(); setShowAddExpense(false); }}
          onClose={() => { setScannedBill(null); setShowAddExpense(false); }}
        />
      )}
      {showScanner && (
        <BillScannerModal group={group} onBillScanned={handleBillScanned} onClose={() => setShowScanner(false)} />
      )}
      {showSettleUp && (
        <SettleUpModal group={group} onSuccess={() => { fetchGroup(); setShowSettleUp(false); }} onClose={() => setShowSettleUp(false)} />
      )}
      {showShare && <ShareModal group={group} onClose={() => setShowShare(false)} />}
    </div>
  );
};

export default GroupPage;
